import type { BattleMockSource } from "../../live/BattleMockSource";
import type { LiveEvent, LiveEventHub } from "../../live/protocol";
import { BattleInteractionRouter, type BattleAction, type BattleGift, type BattleTeam } from "./BattleInteractionRouter";
import { BattleWorld } from "./BattleWorld";

type BattlePhase = "intro" | "playing" | "paused" | "result";
type TeamState = {
  base: number;
  force: number;
  energy: number;
  shield: number;
  likes: number;
  gifts: number;
  members: Set<string>;
  boost: number;
  spring: number;
  lastChargeAt: number;
};
type Attrition = { team: BattleTeam; remaining: number; perSecond: number };

export type BattleSnapshot = {
  phase: BattlePhase;
  elapsed: number;
  remaining: number;
  frontline: number;
  totalEvents: number;
  winner: BattleTeam | "draw" | null;
  teams: Record<BattleTeam, { base: number; force: number; energy: number; shield: number; spring: number; likes: number; gifts: number; members: number }>;
  renderer: string;
  graphics: { fps: number; drawCalls: number; computeCalls: number; triangles: number; gpuSimulation: boolean; gpuVfx: { capacity: number; activeParticles: number; computePasses: number }; gpuTerrain: { vertexSimulation: boolean; vertices: number } };
  crowd: { logicalParticipants: number; visibleParticipants: number; emberVisible: number; aquaVisible: number; capacity: number };
  mechanics: { lastPerfectClashAt: number; emberSpring: number; aquaSpring: number };
  telemetry: {
    lastInputAt: number;
    lastAction: string;
    actionCount: number;
    activePointers: number;
    releaseCount: number;
    cancelCount: number;
    lastPointerType: string;
  };
  controls: Record<string, { x: number; y: number; width: number; height: number }>;
};

const ROUND_SECONDS = 60;
const MAX_FORCE = 100_000;
const $ = <T extends HTMLElement>(id: string): T => {
  const value = document.getElementById(id);
  if (!value) throw new Error(`Missing UI element #${id}`);
  return value as T;
};

export class BattleController {
  readonly #world: BattleWorld;
  readonly #hub: LiveEventHub;
  readonly #router: BattleInteractionRouter;
  readonly #mock: BattleMockSource;
  readonly #seen = new Set<string>();
  readonly #seenQueue: string[] = [];
  readonly #activePointers = new Set<number>();
  readonly #attrition: Attrition[] = [];
  readonly #simulationEnabled: boolean;
  readonly #teams: Record<BattleTeam, TeamState> = {
    ember: teamState(),
    aqua: teamState(),
  };
  #phase: BattlePhase = "intro";
  #elapsed = 0;
  #frontline = 0;
  #totalEvents = 0;
  #winner: BattleTeam | "draw" | null = null;
  #previousTime = performance.now();
  #raf = 0;
  #fps = 60;
  #reducedMotion = matchMedia("(prefers-reduced-motion: reduce)").matches;
  #toastSeconds = 0;
  #lastFeedAt = 0;
  #lastInputAt = 0;
  #lastAction = "none";
  #actionCount = 0;
  #releaseCount = 0;
  #cancelCount = 0;
  #lastPointerType = "none";
  #lastPerfectClashAt = -10;
  #hostSequence = 0;

  constructor(
    world: BattleWorld,
    hub: LiveEventHub,
    router: BattleInteractionRouter,
    mock: BattleMockSource,
    options: { simulationEnabled?: boolean; exposeDebugAPI?: boolean } = {},
  ) {
    this.#world = world;
    this.#hub = hub;
    this.#router = router;
    this.#mock = mock;
    this.#simulationEnabled = options.simulationEnabled ?? true;
    if (!this.#simulationEnabled) {
      this.#teams.ember.force = 0;
      this.#teams.aqua.force = 0;
    }
    this.#hub.subscribe((event) => this.#router.route(event));
    this.#router.subscribe((action) => this.#onAction(action));
    this.#bindUI();
    if (options.exposeDebugAPI ?? true) this.#publishDebugAPI();
    this.#renderHUD();
    this.#raf = requestAnimationFrame((time) => this.#tick(time));
  }

  start(): void {
    this.#reset();
    this.#phase = "playing";
    $("start-screen").hidden = true;
    $("pause-screen").hidden = true;
    $("result-screen").hidden = true;
    if (this.#simulationEnabled) this.#mock.start();
    this.#recordInput("start");
    this.#toast("火绒小队碰上月潮小队：弹幕选边，每次互动都有反馈！", 2.2);
  }

  pause(): void {
    if (this.#phase !== "playing") return;
    this.#phase = "paused";
    this.#activePointers.clear();
    $("pause-screen").hidden = false;
    this.#recordInput("pause");
  }

  resume(): void {
    if (this.#phase !== "paused") return;
    this.#phase = "playing";
    $("pause-screen").hidden = true;
    this.#previousTime = performance.now();
    this.#recordInput("resume");
  }

  seedBattle(count: number): void {
    if (this.#phase !== "playing") this.start();
    this.#mock.seedBattle(count);
    this.#toast(`${Math.min(50_000, Math.max(0, Math.floor(count))).toLocaleString("zh-CN")} 名观众一起铺满梦毯！`);
  }

  hostAction(team: BattleTeam, kind: BattleGift | "charge"): void {
    if (this.#phase !== "playing") return;
    const user = { id: `host-${team}`, displayName: "主播指挥" };
    this.#hostSequence += 1;
    const base = { id: `host-${Date.now()}-${this.#hostSequence}-${kind}`, team, user, at: Date.now() };
    const action: BattleAction = kind === "charge"
      ? { ...base, kind: "charge", amount: 22 }
      : kind === "reinforce"
        ? { ...base, kind: "gift", gift: "reinforce", count: 1, value: 100 }
        : { ...base, kind: "gift", gift: kind, count: 1, value: giftValue(kind) };
    this.#onAction(action);
    this.#recordInput(`${team}-${kind}`);
  }

  snapshot(): BattleSnapshot {
    const swarm = this.#world.swarm.snapshot();
    const graphics = this.#world.graphicsSnapshot();
    return {
      phase: this.#phase,
      elapsed: this.#elapsed,
      remaining: Math.max(0, ROUND_SECONDS - this.#elapsed),
      frontline: this.#frontline,
      totalEvents: this.#totalEvents,
      winner: this.#winner,
      teams: {
        ember: snapshotTeam(this.#teams.ember),
        aqua: snapshotTeam(this.#teams.aqua),
      },
      renderer: this.#world.backend,
      graphics: {
        fps: this.#fps,
        drawCalls: graphics.drawCalls,
        computeCalls: graphics.computeCalls,
        triangles: graphics.triangles,
        gpuSimulation: graphics.gpuSimulation,
        gpuVfx: graphics.gpuVfx,
        gpuTerrain: graphics.gpuTerrain,
      },
      crowd: {
        logicalParticipants: this.#teams.ember.members.size + this.#teams.aqua.members.size,
        visibleParticipants: swarm.emberVisible + swarm.aquaVisible,
        emberVisible: swarm.emberVisible,
        aquaVisible: swarm.aquaVisible,
        capacity: swarm.capacity,
      },
      mechanics: {
        lastPerfectClashAt: this.#lastPerfectClashAt,
        emberSpring: this.#teams.ember.spring,
        aquaSpring: this.#teams.aqua.spring,
      },
      telemetry: {
        lastInputAt: this.#lastInputAt,
        lastAction: this.#lastAction,
        actionCount: this.#actionCount,
        activePointers: this.#activePointers.size,
        releaseCount: this.#releaseCount,
        cancelCount: this.#cancelCount,
        lastPointerType: this.#lastPointerType,
      },
      controls: Object.fromEntries(["control-ember", "control-clash", "control-aqua", "pause-button", "resume-button"]
        .map((id) => {
          const element = document.getElementById(id);
          if (!element || element.offsetParent === null) return null;
          const rect = element.getBoundingClientRect();
          return [id, { x: rect.x, y: rect.y, width: rect.width, height: rect.height }] as const;
        })
        .filter((entry): entry is readonly [string, { x: number; y: number; width: number; height: number }] => entry !== null)),
    };
  }

  inject(event: LiveEvent): void {
    this.#hub.publish(event);
  }

  dispose(): void {
    cancelAnimationFrame(this.#raf);
    this.#mock.stop();
    this.#world.dispose();
  }

  #reset(): void {
    this.#router.reset();
    this.#seen.clear();
    this.#seenQueue.length = 0;
    this.#attrition.length = 0;
    Object.assign(this.#teams.ember, teamState());
    Object.assign(this.#teams.aqua, teamState());
    if (!this.#simulationEnabled) {
      this.#teams.ember.force = 0;
      this.#teams.aqua.force = 0;
    }
    this.#elapsed = 0;
    this.#frontline = 0;
    this.#totalEvents = 0;
    this.#winner = null;
    this.#lastPerfectClashAt = -10;
    this.#hostSequence = 0;
    this.#toastSeconds = 0;
    this.#world.reset();
  }

  #tick(timeMs: number): void {
    const dt = Math.min(0.05, Math.max(0, (timeMs - this.#previousTime) / 1_000));
    this.#previousTime = timeMs;
    if (dt > 0) this.#fps += ((1 / dt) - this.#fps) * Math.min(1, dt * 3.5);
    const time = timeMs / 1_000;
    if (this.#phase === "playing") {
      this.#elapsed += dt;
      if (this.#simulationEnabled) this.#mock.update(dt);
      this.#stepBattle(dt, time);
    }
    const frame = this.#worldFrame();
    this.#world.update(dt, time, frame);
    this.#world.render();
    this.#toastSeconds = Math.max(0, this.#toastSeconds - dt);
    if (this.#toastSeconds === 0) $("game-toast").classList.remove("is-visible");
    this.#renderHUD();
    this.#raf = requestAnimationFrame((next) => this.#tick(next));
  }

  #stepBattle(dt: number, _time: number): void {
    const ember = this.#teams.ember;
    const aqua = this.#teams.aqua;
    const emberActive = this.#simulationEnabled || ember.members.size > 0 || ember.force > 0;
    const aquaActive = this.#simulationEnabled || aqua.members.size > 0 || aqua.force > 0;
    if (emberActive) ember.force = Math.min(MAX_FORCE, ember.force + (3.2 + Math.sqrt(ember.members.size) * 0.18) * dt);
    if (aquaActive) aqua.force = Math.min(MAX_FORCE, aqua.force + (3.2 + Math.sqrt(aqua.members.size) * 0.18) * dt);
    ember.boost = Math.max(0, ember.boost - dt);
    aqua.boost = Math.max(0, aqua.boost - dt);
    ember.shield = Math.max(0, ember.shield - dt * 1.4);
    aqua.shield = Math.max(0, aqua.shield - dt * 1.4);
    this.#stepAttrition(dt);
    const emberPower = Math.log2(ember.force + 2) + (ember.boost > 0 ? 1.1 : 0);
    const aquaPower = Math.log2(aqua.force + 2) + (aqua.boost > 0 ? 1.1 : 0);
    const pressure = Math.tanh((emberPower - aquaPower) * 0.72);
    this.#frontline = Math.max(-3.35, Math.min(3.35, this.#frontline + pressure * dt * 0.72));
    const clash = Math.min(14, Math.sqrt(Math.min(ember.force, aqua.force)) * 0.12) * dt;
    ember.force = Math.max(emberActive ? 20 : 0, ember.force - clash * (1 + Math.max(0, -pressure) * 0.45));
    aqua.force = Math.max(aquaActive ? 20 : 0, aqua.force - clash * (1 + Math.max(0, pressure) * 0.45));
    if (this.#frontline > 2.72) this.#damage("aqua", (this.#frontline - 2.72) * 25 * dt);
    if (this.#frontline < -2.72) this.#damage("ember", (-this.#frontline - 2.72) * 25 * dt);
    if (ember.base <= 0 || aqua.base <= 0 || this.#elapsed >= ROUND_SECONDS) this.#finish();
  }

  #onAction(action: BattleAction): void {
    if (this.#phase !== "playing" || this.#seen.has(action.id)) return;
    this.#seen.add(action.id);
    this.#seenQueue.push(action.id);
    if (this.#seenQueue.length > 16_384) {
      const expired = this.#seenQueue.shift();
      if (expired) this.#seen.delete(expired);
    }
    this.#totalEvents += 1;
    const team = this.#teams[action.team];
    team.members.add(action.user.id);
    if (action.kind === "join") {
      team.force = Math.min(MAX_FORCE, team.force + 3);
      this.#feed(action.team, `${action.user.displayName} 加入${teamName(action.team)}`, false);
    } else if (action.kind === "charge") {
      const visualTime = performance.now() / 1_000;
      const opposingTeam = action.team === "ember" ? this.#teams.aqua : this.#teams.ember;
      const springPower = Math.min(60, team.spring);
      team.spring = Math.max(0, team.spring - springPower);
      team.force = Math.min(MAX_FORCE, team.force + action.amount + springPower * 1.65);
      team.boost = Math.max(team.boost, springPower > 0 ? 2.45 : 1.6);
      if (springPower > 0) {
        this.#world.triggerSlingshot(action.team, visualTime, springPower / 60);
        this.#flashImpact(action.team === "ember" ? "ember" : "aqua");
        this.#toast(`${teamName(action.team)}释放枕头弹弓！守护弹力全部转成推动力！`, 1.4);
        this.#feed(action.team, `🛏 守→冲连招：枕头弹弓 +${Math.round(springPower * 1.65)}`, true);
      } else {
        this.#world.triggerCharge(action.team, visualTime);
        this.#feed(action.team, `${teamName(action.team)}用弹幕推动梦缝`, false);
      }
      const perfectClash = visualTime - opposingTeam.lastChargeAt < 0.72 && visualTime - this.#lastPerfectClashAt > 1.35;
      team.lastChargeAt = visualTime;
      if (perfectClash) {
        this.#lastPerfectClashAt = visualTime;
        this.#frontline *= 0.78;
        this.#teams.ember.force = Math.max(20, this.#teams.ember.force - 16);
        this.#teams.aqua.force = Math.max(20, this.#teams.aqua.force - 16);
        this.#world.triggerPerfectClash(visualTime);
        this.#flashImpact("perfect");
        this.#toast("完美对撞！梦缝闪亮，前排毛球被羽毛波弹起！", 1.65);
        this.#feed(action.team, "✨ 双方同步推动触发完美对撞", true);
      }
    } else if (action.kind === "guard") {
      team.shield = Math.min(180, team.shield + action.amount);
      team.spring = Math.min(100, team.spring + action.amount);
      this.#feed(action.team, `${teamName(action.team)}叠盾并蓄积枕头弹力`, false);
    } else if (action.kind === "support") {
      const before = Math.floor(team.energy / 20);
      team.likes += action.likes;
      team.energy += action.likes;
      const squads = Math.floor(team.energy / 20) - before;
      if (squads > 0) {
        team.force = Math.min(MAX_FORCE, team.force + squads * 18);
        this.#feed(action.team, `${action.likes} 赞聚拢 ${squads} 组毛球`, true);
      } else this.#feed(action.team, `${action.likes} 赞点亮绒力进度`, false);
    } else if (action.kind === "follow") {
      team.force = Math.min(MAX_FORCE, team.force + 35);
      team.shield = Math.min(180, team.shield + 12);
      this.#feed(action.team, `新关注为${teamName(action.team)}点亮闪光入场`, true);
    } else {
      this.#applyGift(action.team, action.gift, action.count, performance.now() / 1_000);
    }
  }

  #applyGift(teamId: BattleTeam, gift: BattleGift, count: number, time: number): void {
    const team = this.#teams[teamId];
    const target: BattleTeam = teamId === "ember" ? "aqua" : "ember";
    team.gifts += count;
    if (gift === "reinforce") {
      team.force = Math.min(MAX_FORCE, team.force + 180 * count);
      team.boost = Math.max(team.boost, 2.4);
      this.#toast(`${teamName(teamId)}收到抱枕补给：固定增加 180 绒力！`);
      this.#feed(teamId, "🎁 抱枕补给带来一群睡衣毛球", true);
    } else if (gift === "artillery") {
      this.#teams[target].force = Math.max(20, this.#teams[target].force - 190 * count);
      this.#damage(target, 58 * count);
      this.#world.triggerArtillery(target, time);
      this.#flashImpact(teamId === "ember" ? "ember" : "aqua");
      this.#toast(`${teamName(teamId)}送来星星枕雨，固定触发范围弹跳！`);
      this.#feed(teamId, "💫 星星枕雨推动对面梦缝", true);
    } else if (gift === "flood") {
      this.#queueAttrition(target, 420 * count, 3.05);
      this.#damage(target, 88 * count);
      this.#world.triggerFlood(target, time);
      this.#flashImpact("flood");
      this.#toast(`${teamName(teamId)}铺开月光水毯，水波推开毛球并持续改变绒力！`, 2.4);
      this.#feed(teamId, "🌊 月光水毯：GPU 水波推动毛球", true);
    } else if (gift === "catrush") {
      this.#queueAttrition(target, 480 * count, 2.35);
      this.#damage(target, 92 * count);
      this.#world.triggerCatRush(teamId, time);
      this.#flashImpact("catrush");
      this.#toast(`${teamName(teamId)}请来 CozyRoom 巨猫 Mango，沿梦毯快乐飞奔！`, 2.5);
      this.#feed(teamId, "🐈 Mango 飞奔：爪印路径持续推动梦缝", true);
    } else if (gift === "mech") {
      this.#queueAttrition(target, 360 * count, 3.8);
      this.#damage(target, 82 * count);
      this.#world.triggerBiped(teamId, time);
      this.#flashImpact("mech");
      this.#toast(`${teamName(teamId)}启动双足 IK 玩具，绒毯重踏带起星光！`, 2.4);
      this.#feed(teamId, "🤖 双足 IK 玩具：自适应步态推动", true);
    } else if (gift === "rift") {
      this.#queueAttrition(target, 520 * count, 2.8);
      this.#damage(target, 108 * count);
      this.#world.triggerRift(target, time);
      this.#flashImpact("rift");
      this.#toast(`${teamName(teamId)}翻卷柔软地毯，对面毛球随软垫弹起！`, 2.4);
      this.#feed(teamId, "🪨 地毯翻卷：GPU 顶点变形弹起毛球", true);
    } else if (gift === "hound") {
      this.#queueAttrition(target, 500 * count, 2.65);
      this.#damage(target, 106 * count);
      this.#world.triggerHound(teamId, time);
      this.#flashImpact("hound");
      this.#toast(`${teamName(teamId)}启动四足 IK 玩具，贴地飞奔推动梦缝！`, 2.4);
      this.#feed(teamId, "🐕 四足 IK 玩具：高速碰撞弹起", true);
    } else {
      this.#queueAttrition(target, 620 * count, 3.45);
      this.#damage(target, 125 * count);
      this.#world.triggerTornado(target, time);
      this.#flashImpact("tornado");
      this.#toast(`${teamName(teamId)}召唤棉花龙卷风，把对面毛球卷进漏斗！`, 2.1);
      this.#feed(teamId, "🌪 棉花龙卷风：GPU 旋转卷起毛球", true);
    }
  }

  #queueAttrition(team: BattleTeam, amount: number, seconds: number): void {
    this.#attrition.push({ team, remaining: amount, perSecond: amount / Math.max(0.1, seconds) });
  }

  #stepAttrition(dt: number): void {
    for (let index = this.#attrition.length - 1; index >= 0; index -= 1) {
      const event = this.#attrition[index]!;
      const loss = Math.min(event.remaining, event.perSecond * dt);
      this.#teams[event.team].force = Math.max(20, this.#teams[event.team].force - loss);
      event.remaining -= loss;
      if (event.remaining <= 0.01 || this.#teams[event.team].force <= 20) this.#attrition.splice(index, 1);
    }
  }

  #damage(teamId: BattleTeam, amount: number): void {
    const team = this.#teams[teamId];
    const absorbed = Math.min(team.shield, amount);
    team.shield -= absorbed;
    team.base = Math.max(0, team.base - (amount - absorbed));
  }

  #finish(): void {
    if (this.#phase !== "playing") return;
    this.#phase = "result";
    this.#mock.stop();
    const emberScore = this.#teams.ember.base + this.#teams.ember.force * 0.05;
    const aquaScore = this.#teams.aqua.base + this.#teams.aqua.force * 0.05;
    this.#winner = Math.abs(emberScore - aquaScore) < 1 ? "draw" : emberScore > aquaScore ? "ember" : "aqua";
    $("result-screen").hidden = false;
    $("result-title").textContent = this.#winner === "draw" ? "梦缝停在正中央！" : `${teamName(this.#winner)}铺满更多梦毯！`;
    $("result-copy").textContent = `共 ${this.#totalEvents.toLocaleString("zh-CN")} 次直播互动，${this.#teams.ember.gifts + this.#teams.aqua.gifts} 件礼物均已获得固定反馈。`;
  }

  #worldFrame() {
    return {
      emberVisible: visibleForce(this.#teams.ember.force),
      aquaVisible: visibleForce(this.#teams.aqua.force),
      frontline: this.#frontline,
      reducedMotion: this.#reducedMotion,
      emberBase: this.#teams.ember.base,
      aquaBase: this.#teams.aqua.base,
      emberShield: this.#teams.ember.shield,
      aquaShield: this.#teams.aqua.shield,
    };
  }

  #renderHUD(): void {
    const ember = this.#teams.ember;
    const aqua = this.#teams.aqua;
    $("battle-time").textContent = String(Math.ceil(Math.max(0, ROUND_SECONDS - this.#elapsed)));
    $("ember-health").textContent = String(Math.round(ember.base));
    $("aqua-health").textContent = String(Math.round(aqua.base));
    $("ember-health-fill").style.width = `${ember.base / 10}%`;
    $("aqua-health-fill").style.width = `${aqua.base / 10}%`;
    $("ember-force").textContent = Math.round(ember.force).toLocaleString("zh-CN");
    $("aqua-force").textContent = Math.round(aqua.force).toLocaleString("zh-CN");
    $("ember-members").textContent = ember.members.size.toLocaleString("zh-CN");
    $("aqua-members").textContent = aqua.members.size.toLocaleString("zh-CN");
    $("ember-energy").style.width = `${Math.max((ember.energy % 20) * 5, ember.spring)}%`;
    $("aqua-energy").style.width = `${Math.max((aqua.energy % 20) * 5, aqua.spring)}%`;
    $("frontline-marker").style.left = `${50 - (this.#frontline / 3.35) * 43}%`;
    $("phase-chip").textContent = this.#phase === "intro"
      ? "等待入梦"
      : this.#phase === "paused"
        ? "梦境暂停"
        : this.#phase === "result"
          ? "本轮结束"
          : Math.abs(this.#frontline) < 0.55
            ? "梦缝激烈交锋"
            : this.#frontline > 0 ? "火绒撕开梦缝" : "月潮反推梦缝";
  }

  #feed(team: BattleTeam, message: string, important: boolean): void {
    const now = performance.now();
    if (!important && now - this.#lastFeedAt < 420) return;
    this.#lastFeedAt = now;
    const item = document.createElement("div");
    item.dataset.team = team;
    const badge = document.createElement("b");
    badge.textContent = team === "ember" ? "火" : "潮";
    const copy = document.createElement("span");
    copy.textContent = message;
    item.append(badge, copy);
    const feed = $("event-feed");
    feed.prepend(item);
    while (feed.children.length > 3) feed.lastElementChild?.remove();
    window.setTimeout(() => item.classList.add("is-visible"), 0);
    window.setTimeout(() => item.remove(), 5_600);
  }

  #toast(message: string, seconds = 1.5): void {
    const toast = $("game-toast");
    toast.textContent = message;
    toast.classList.add("is-visible");
    this.#toastSeconds = seconds;
  }

  #flashImpact(kind: "ember" | "aqua" | "perfect" | "tornado" | "flood" | "rift" | "catrush" | "mech" | "hound"): void {
    const flash = $("impact-flash");
    flash.className = "impact-flash";
    void flash.offsetWidth;
    flash.classList.add(`is-${kind}`);
  }

  #bindUI(): void {
    this.#bindActivation("start-button", () => this.start(), "start");
    this.#bindActivation("restart-button", () => this.start(), "restart");
    this.#bindActivation("pause-button", () => this.pause(), "pause");
    this.#bindActivation("resume-button", () => this.resume(), "resume");
    this.#bindActivation("control-ember", () => this.hostAction("ember", "reinforce"), "ember-reinforce");
    this.#bindActivation("control-clash", () => {
      this.hostAction("ember", "charge");
      this.hostAction("aqua", "charge");
    }, "double-charge");
    this.#bindActivation("control-aqua", () => this.hostAction("aqua", "reinforce"), "aqua-reinforce");
    $("motion-toggle").addEventListener("change", (event) => {
      this.#reducedMotion = (event.currentTarget as HTMLInputElement).checked;
    });
    window.addEventListener("resize", () => this.#world.resize());
    window.addEventListener("keydown", (event) => {
      if (event.repeat) return;
      if (event.key === "1") this.hostAction("ember", "reinforce");
      if (event.key === "2") this.hostAction("aqua", "reinforce");
      if (event.code === "Space") {
        event.preventDefault();
        this.hostAction(this.#frontline <= 0 ? "ember" : "aqua", "charge");
      }
    });
  }

  #bindActivation(id: string, action: () => void, label: string): void {
    const target = $(id);
    let touchHandledAt = -1_000;
    target.addEventListener("touchstart", (event) => {
      event.preventDefault();
      touchHandledAt = performance.now();
      for (const touch of event.changedTouches) this.#activePointers.add(100_000 + touch.identifier);
      this.#lastPointerType = "touch";
      action();
      this.#recordInput(label);
    }, { passive: false });
    target.addEventListener("touchend", (event) => {
      event.preventDefault();
      for (const touch of event.changedTouches) this.#activePointers.delete(100_000 + touch.identifier);
      this.#releaseCount += 1;
    }, { passive: false });
    target.addEventListener("touchcancel", (event) => {
      for (const touch of event.changedTouches) this.#activePointers.delete(100_000 + touch.identifier);
      this.#cancelCount += 1;
    });
    target.addEventListener("pointerdown", (event) => {
      if (event.pointerType === "mouse" || performance.now() - touchHandledAt < 100) return;
      this.#activePointers.add(event.pointerId);
      this.#lastPointerType = event.pointerType;
      action();
      this.#recordInput(label);
    });
    target.addEventListener("pointerup", (event) => {
      if (this.#activePointers.delete(event.pointerId)) this.#releaseCount += 1;
    });
    target.addEventListener("pointercancel", (event) => {
      this.#activePointers.delete(event.pointerId);
      this.#cancelCount += 1;
    });
    target.addEventListener("click", () => {
      if (performance.now() - touchHandledAt < 500) return;
      this.#lastPointerType = "mouse";
      action();
      this.#recordInput(label);
    });
  }

  #recordInput(action: string): void {
    this.#lastInputAt = performance.now();
    this.#lastAction = action;
    this.#actionCount += 1;
  }

  #publishDebugAPI(): void {
    const api = {
      start: () => this.start(),
      pause: () => this.pause(),
      resume: () => this.resume(),
      seedBattle: (count: number) => this.seedBattle(count),
      seedCrowd: (count: number) => this.seedBattle(count),
      support: (team: BattleTeam, kind: BattleGift | "charge") => this.hostAction(team, kind),
      suckLane: () => this.hostAction("ember", "tornado"),
      inject: (event: LiveEvent) => this.inject(event),
      snapshot: () => this.snapshot(),
    };
    window.__BATTLE_GAME__ = api;
    window.__TORNADO_GAME__ = api;
  }
}

function teamState(): TeamState {
  return { base: 1_000, force: 220, energy: 0, shield: 0, spring: 0, likes: 0, gifts: 0, members: new Set(), boost: 0, lastChargeAt: -10 };
}

function snapshotTeam(team: TeamState) {
  return { base: team.base, force: team.force, energy: team.energy, shield: team.shield, spring: team.spring, likes: team.likes, gifts: team.gifts, members: team.members.size };
}

function visibleForce(force: number): number {
  return force <= 0 ? 0 : Math.min(2_048, Math.max(64, Math.floor(force * 2)));
}

function giftValue(gift: BattleGift): number {
  if (gift === "tornado") return 1_000;
  if (gift === "hound") return 900;
  if (gift === "rift") return 800;
  if (gift === "mech") return 750;
  if (gift === "catrush") return 650;
  if (gift === "flood") return 500;
  if (gift === "artillery") return 300;
  return 100;
}

function teamName(team: BattleTeam): string {
  return team === "ember" ? "火绒小队" : "月潮小队";
}

declare global {
  interface Window {
    __BATTLE_GAME__: {
      start: () => void;
      pause: () => void;
      resume: () => void;
      seedBattle: (count: number) => void;
      seedCrowd: (count: number) => void;
      support: (team: BattleTeam, kind: BattleGift | "charge") => void;
      suckLane: () => void;
      inject: (event: LiveEvent) => void;
      snapshot: () => BattleSnapshot;
    };
    __TORNADO_GAME__: Window["__BATTLE_GAME__"];
  }
}
